import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  assignPermission,
  fetchedAssignedPermissions,
  fetchRoles,
  removeAssignedPermission,
} from '../../slices/FbPermission';
import InfiniteScroll from 'react-infinite-scroll-component';
import DeleteModal from '../../../../bmr/components/common/BaseModal/DeleteModal';
import { notify } from '../../../../bmr/utils/methods/notification';
import NoDataCard from '../../../../bmr/components/NoDataCard';

const FbPermission = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
 const [selectedRoles, setSelectedRoles] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total_pages: 1,
    total_count: 0,
  });

  const handleRoleRemoveClick = (roleId, initId) => {
    setRoleToDelete({ roleId, initId });
    setOpenModal(true);
  };

  const handleAccept = async () => {
    try {
      if (roleToDelete) {
        await dispatch(removeAssignedPermission({
          template_id: id,
          permission_id: roleToDelete.initId
        })).unwrap();
         loadRoles();
       await loadAssignedPermissions(1);
        notify.success("Permission removed successfully"); //@info update in thunk INPROGRESS
      }
    } catch (error) {
      notify.error(error?.message || error?.data?.message || "Failed to remove permission");
    } finally {
      setOpenModal(false);
      setRoleToDelete(null);
    }
  };

  const handleSubmit = async () => {
    if (selectedRoles.length === 0) {
      notify.warning("Please select at least one role"); //@info update in thunk INPROGRESS
      return;
    }

    try {
      await dispatch(
        assignPermission({
          template_id: id,
          role_ids: selectedRoles.map((role) => role.value),
        })
      ).unwrap();
       loadRoles();
      setSelectedRoles([]);await loadAssignedPermissions(1);
      notify.success("Permissions assigned successfully"); //@info update in thunk INPROGRESS
    } catch (error) {
      notify.error(error?.message || error?.data?.message || "Failed to assign permissions");
    }
  };

  const loadAssignedPermissions = async (pageNum = 1) => {
    if (loadingAssigned) return;
    setLoadingAssigned(true);
    try {
      const res = await dispatch(
        fetchedAssignedPermissions({
          id: id,
          page: pageNum,
          per_page: pagination.per_page,
        })
      ).unwrap();

      if (res?.data) {
        setPagination({
          page: res.data.page,
          per_page: res.data.per_page,
          total_pages: res.data.total_pages,
          total_count: res.data.total_count,
        });
  if (pageNum === 1) {
          setAssignedRoles(res?.data?.items ?? []);
        } else {
          setAssignedRoles((prev) => [...prev, ...(res?.data?.items ?? [])]);
        }
      }
    } catch (err) {
      console.error('Error fetching assigned permissions:', err);
      notify.error("Failed to load permissions");
    } finally {
      setLoadingAssigned(false);
    }
  };

  const fetchMoreData = async () => {
    if (loadingAssigned || pagination.page >= pagination.total_pages) return;
    const nextPage = pagination.page + 1;
    await loadAssignedPermissions(nextPage);
  };

  const loadRoles = async () => {
    setLoading(true);
    try {
      const res = await dispatch(fetchRoles({'page':'fb','temp_id':id})).unwrap();
      const newRoles = res.data.map((role) => ({
        label: role.title,
        value: role.id,
      }));

      setRoles(newRoles);
    } catch (err) {
      console.error('Failed to fetch roles:', err); //@info update in thunk INPROGRESS
      notify.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

 const filteredRoles = roles.filter(role => 
    !assignedRoles.some(assigned => 
      assigned.role_id === role.value || assigned.id === role.value
    )
  );
  useEffect(() => {
    setRoles([]);
    setAssignedRoles([]); 
    loadRoles();
    loadAssignedPermissions(1);
  }, [id]); 

  return (
    <Box className="bg-white rounded-2xl p-3">
      <Typography variant="subtitle1" fontWeight="bold">
        Template Permissions
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
        Search and add users roles to the template who will be responsible for approving the template.
      </Typography>

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={filteredRoles}
        value={selectedRoles}
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(e, newValue) => setSelectedRoles(newValue)}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Roles"
            placeholder="Select..."
            sx={{
              "& .MuiInputLabel-root": {
                marginTop: "0.25rem",
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} color="inherit" /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    <Divider sx={{ my: 1 ,mt:'1.6rem'}} />
    <Box sx={{border:'1px solid', borderColor:'#DDDDDD'}}>
      <Typography variant="subtitle1" fontWeight="400" sx={{ mb: 1, mt: 3 , ml:1}}>
        Permissions Given To:
      </Typography>

      {assignedRoles.length > 0 ? (
        <InfiniteScroll
          dataLength={assignedRoles.length} 
          next={fetchMoreData}
          hasMore={pagination.page < pagination.total_pages}
          loader={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          }
          height={400}
          style={{ overflow: 'auto' }}
        >
          {assignedRoles.map((item, index) => (
            <Box
              key={`${item.role_id || item.id}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 1,
                borderBottom: '1px solid #eee',
              }}
            >
              <Typography variant="body1" sx={{color:'#828282'}}>
                {item?.role_details?.title || item.title}
              </Typography>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleRoleRemoveClick(item.role_id || item.id, item.id)}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </InfiniteScroll>
      ) : loadingAssigned ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <NoDataCard />
      )}
</Box>
      <Box className="bg-white py-3 rounded-xl w-full flex justify-end gap-2 h-[72px] border-t border-gray-200">
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={selectedRoles.length === 0 || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>

      <DeleteModal
        open={openModal}
        setClose={setOpenModal}
        title="Are you sure you want to Delete?"
        handleSuccess={handleAccept}
        handleRejection={() => {
          setOpenModal(false);
          setRoleToDelete(null);
        }}
        buttonText="Delete"
      />
    </Box>
  );
};

export default FbPermission;